import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Alert,
  message,
  Switch,
  Space,
  Tag
} from 'antd';
import { ArrowDownOutlined } from '@ant-design/icons';
import { authAxios } from '../utils/session_utils';
import { YouTubeLiveStream, StreamingChannel } from './types';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

interface StreamBuilderProps {
  stream?: YouTubeLiveStream | null;
  distributionPlatformData?: any[];
  onClose: () => void;
  onSaved: (streamId: string) => void;
}

const StreamBuilder: React.FC<StreamBuilderProps> = ({ stream, distributionPlatformData = [], onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [channels, setChannels] = useState<StreamingChannel[]>([]);

  // Get user from outlet context
  const { user } = useOutletContext<{ user: any }>();

  const isEditMode = !!stream;

  // Channels are seeded by SQL, never created here: a channel id is permanent
  // (partners key their ingest config to it), so the UI only ever picks an
  // existing one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authAxios({
          method: 'GET',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/channels`
        });
        if (!cancelled) setChannels(res?.data?.channels ?? []);
      } catch (err) {
        // Non-fatal: the rest of the form still works, the picker is just empty.
        console.error('Error loading channels:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // YouTube-only fields (start time, privacy, captions) are hidden for Amagi
  // HLS-pull streams, which are driven by a schedule CSV instead. Distribution
  // platforms stay visible for both — Amagi streams need one to resolve EPG art.
  const outputTarget = Form.useWatch('output_target', form);
  const isAmagi = outputTarget === 'amagi_hls';

  useEffect(() => {
    if (stream) {
      form.setFieldsValue({
        name: stream.name,
        description: stream.description,
        scheduled_start_time: stream.scheduled_start_time ? dayjs(stream.scheduled_start_time) : null,
        privacy_status: stream.privacy_status,
        cea_embedded_captions: stream.cea_embedded_captions ?? false,
        distribution_platform_ids: stream.distribution_platform_ids || [],
        output_target: stream.output_target || 'youtube',
        channel_id: stream.channel_id
      });
    } else {
      // Reset form for new stream
      form.resetFields();
      // Set default value for cea_embedded_captions
      form.setFieldsValue({
        cea_embedded_captions: false,
        distribution_platform_ids: [],
        output_target: 'youtube'
      });
    }
  }, [stream, form]);

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const values = await form.validateFields();
      
      const url = isEditMode && stream
        ? `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}`
        : `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await authAxios({
        method,
        url,
        data: {
          name: values.name,
          user_id: user.id,
          description: values.description,
          scheduled_start_time: values.scheduled_start_time?.toISOString(),
          privacy_status: values.privacy_status,
          cea_embedded_captions: values.cea_embedded_captions ?? false,
          distribution_platform_ids: values.distribution_platform_ids || [],
          output_target: values.output_target || 'youtube',
          // '' rather than dropped, so clearing the picker actually detaches the
          // stream from its channel and the EPG returns to its defaults.
          channel_id: values.channel_id || ''
        }
      });

      message.success(`Stream ${isEditMode ? 'updated' : 'created'} successfully`);
      const streamId = isEditMode && stream ? stream.id : response?.data?.streamId;
      if (!streamId) {
        throw new Error('Stream ID not returned from save');
      }
      onSaved(streamId);
    } catch (error) {
      console.error('Error saving stream:', error);
      setError('Failed to save stream: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError('');
    onClose();
  };

  const handleSetNow = () => {
    form.setFieldsValue({ scheduled_start_time: dayjs() });
  };

  const handleSetNowClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handleSetNow();
  };

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={() => setError('')}
          style={{ marginBottom: '16px' }}
        />
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Stream Name"
          rules={[{ required: true, message: 'Please enter stream name' }]}
        >
          <Input placeholder="Enter stream name" maxLength={100} />
        </Form.Item>
        
        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: 'Please enter stream description' }]}
        >
          <TextArea 
            rows={4} 
            placeholder="Enter stream description" 
            maxLength={5000}
            showCount
          />
        </Form.Item>
        
        {!isAmagi && (
        <Form.Item
          name="scheduled_start_time"
          label={
            <Space size="small">
              <span>Scheduled Start Time</span>
              <Tag
                color="blue"
                onMouseDown={handleSetNowClick}
                onClick={handleSetNowClick}
                style={{ cursor: 'pointer' }}
              >
                Set to Now <ArrowDownOutlined />
              </Tag>
            </Space>
          }
          rules={[
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const isValid = value.isAfter(dayjs().subtract(30, 'seconds'));
                return isValid
                  ? Promise.resolve()
                  : Promise.reject(new Error('Start time must be in the future (or use Set to Now)'));
              }
            }
          ]}
        >
          <DatePicker 
            showTime 
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="Select start time"
            style={{ width: '100%' }}
            disabledDate={(current) => current && current.isBefore(dayjs().startOf('day'))}
          />
        </Form.Item>
        )}

        <Form.Item
          name="output_target"
          label="Output Target"
          tooltip="YouTube pushes RTMP to YouTube Live. Amagi (HLS pull) serves an HLS feed from the playout server that Amagi pulls — no RTMP needed; assign a playout server and import a schedule CSV instead."
        >
          <Select>
            <Option value="youtube">YouTube (RTMP push)</Option>
            <Option value="amagi_hls">Amagi (HLS pull)</Option>
          </Select>
        </Form.Item>

        {/* Also applies to Amagi streams: the platform scopes which tagged
            thumbnails become EPG programme artwork. */}
        <Form.Item
          name="distribution_platform_ids"
          label="Distribution Platforms"
          tooltip="Defines the video pool for OAuth (auto) mode, and selects which platform's tagged thumbnails are used as EPG programme artwork."
        >
          <Select
            mode="multiple"
            placeholder="Select distribution platforms"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {distributionPlatformData.map((platform: any) => (
              <Option key={platform.id} value={platform.id}>
                {platform.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* The channel supplies the EPG's channel id, display name and
            language. Its id is permanent and partner-facing, so channels are
            seeded by a developer and only selected here. */}
        <Form.Item
          name="channel_id"
          label="Channel"
          tooltip="The channel this stream broadcasts. Supplies the EPG's permanent channel id, its viewer-facing name, and its language. Leave blank for streams with no channel identity (e.g. YouTube), which fall back to the stream name above."
        >
          <Select
            placeholder="Select channel"
            allowClear
            showSearch
            optionFilterProp="children"
            notFoundContent={channels.length === 0 ? 'No channels defined yet' : undefined}
          >
            {channels.map((channel) => (
              <Option key={channel.id} value={channel.id}>
                {channel.display_name} ({channel.id})
              </Option>
            ))}
          </Select>
        </Form.Item>

        {!isAmagi && (
        <>
        <Form.Item
          name="privacy_status"
          label="Privacy Status"
          rules={[{ required: true, message: 'Please select privacy status' }]}
        >
          <Select placeholder="Select privacy status">
            <Option value="public">Public</Option>
            <Option value="unlisted">Unlisted</Option>
            <Option value="private">Private</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="cea_embedded_captions"
          label="CEA Embedded Captions"
          valuePropName="checked"
          tooltip="Enabling this would trigger transcoding flow of videos with embedded SCC captions. 
          Keep it disabled if you want to stream without captions (or Auto captions in youtube)."
        >
          <Switch />
        </Form.Item>
        </>
        )}
      </Form>

      <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={handleCancel}>
          Close
        </Button>
        <Button 
          type="primary" 
          loading={loading}
          onClick={handleSave}
        >
          {isEditMode ? 'Save Details' : 'Create Stream'}
        </Button>
      </Space>
    </div>
  );
};

export default StreamBuilder;
